import { BrandApi } from "./brand-api";
import { $$ } from "@tento-ui/fastdom";
import {
  type BlogCategory,
  type BlogPost,
} from "../blog/blog-data";
import type {
  BlogPage,
  BlogIndexRoute,
  BlogVariantConfig,
} from "../blog/blog-variant";
import type {
  JsonObject,
  JsonValue,
  PageModel,
  PageNode,
} from "@tento-ui/brand/page-model";
import { PageModel as PageModelParser } from "@tento-ui/brand/page-model";

export class BlogIndexController {
  private state: BlogIndexController.State = {
    searchQuery: "",
    categoryFilter: null as Option<BlogCategory>,
    displayCount: 10,
    totalPosts: 0,
    featuredPosts: [] as BlogPost[],
    posts: [] as BlogPost[],
  };

  private $root = document.createElement("div");
  private onNavigate: (slug: string) => void;
  private fetchGeneration = 0;
  private searchDebounceTimer: Option<number> = null;
  private delegate: Option<BlogPage.Delegate> = null;
  private pagelet: Option<HTMLElement> = null;

  constructor(
    private config: BlogVariantConfig,
    onNavigate: (slug: string) => void,
    initialState: Option<BlogIndexController.State>,
  ) {
    this.onNavigate = onNavigate;
    if (initialState) {
      this.hydrateState(initialState);
      // In mock mode the SSR shell still injects the real dataset; refetch so
      // the mock posts (selected server-side by the `lona_blog_mock` cookie)
      // replace them on first paint — and reveal only once that lands.
      if (blogMockCookiePresent()) {
        this.refetch();
      } else {
        // SSR data is final; reveal after the pagelet's fit-measure settles.
        this.scheduleReveal();
      }
    } else {
      this.init();
    }
  }

  get element(): HTMLElement {
    return this.$root;
  }

  hydrateState(state: BlogIndexController.State): void {
    this.state = state;
    this.renderPagelet();
  }

  private async init() {
    await this.refetch();
  }

  private createPagelet() {
    this.delegate = {
      onSearch: (query: string) => {
        this.state.searchQuery = query;
        this.scheduleSearchRefetch();
      },
      onFilterCategory: (category: BlogCategory) => {
        this.cancelSearchDebounce();
        this.state.categoryFilter =
          category === this.state.categoryFilter ? null : category;
        this.state.displayCount = 10;
        this.refetch();
      },
      onSeeMore: () => {
        this.cancelSearchDebounce();
        this.state.displayCount += 10;
        this.refetch();
      },
      onCardClick: (slug: string) => {
        this.onNavigate(slug);
      },
    };
    this.pagelet = this.config.routes.index.make(this.makeProps(), this.delegate);
    this.$root.appendChild(this.pagelet);
  }

  private scheduleSearchRefetch() {
    this.cancelSearchDebounce();
    this.searchDebounceTimer = window.setTimeout(() => {
      this.searchDebounceTimer = null;
      this.refetch();
    }, 180);
  }

  private cancelSearchDebounce() {
    if (this.searchDebounceTimer == null) return;
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = null;
  }

  private async refetch() {
    const generation = ++this.fetchGeneration;
    const nextState = await this.fetchState();
    if (generation !== this.fetchGeneration) return;

    if (nextState) {
      this.state = nextState;
      this.renderPagelet();
    }
    // Data has settled (or the fetch failed and we keep the SSR data) — reveal
    // the blog. Superseded generations returned above, so a newer fetch reveals.
    this.scheduleReveal();
  }

  private async fetchState(): Promise<Option<BlogIndexController.State>> {
    const result = await BrandApi.getResult("/api/public/brand/pages", {
      query: {
        route: "/",
        query: this.state.searchQuery || null,
        category: this.state.categoryFilter,
        limit: this.state.displayCount,
        offset: null,
      },
    });
    const page = result.ok();
    if (!page) return null;
    return BlogIndexController.stateFromPageModel(PageModelParser.parse(page));
  }

  private makeProps(): BlogPage.Props {
    return {
      posts: this.state.posts,
      featuredPosts: this.state.featuredPosts,
      displayCount: this.state.displayCount,
      totalPosts: this.state.totalPosts,
      searchQuery: this.state.searchQuery,
      categoryFilter: this.state.categoryFilter,
    };
  }

  private renderPagelet(): void {
    if (this.pagelet && this.delegate) {
      this.pagelet = this.config.routes.index.bind(
        this.pagelet,
        this.makeProps(),
        this.delegate,
      );
      return;
    }

    this.createPagelet();
  }

  // Fade the pagelet in once the layout is stable. Going through a fastdom
  // measure -> mutate keeps the reveal in the same flush as the pagelet's own
  // search-fit measure/mutate, so the blog reveals already in its final
  // position — no search-bar show/hide flash, no 1->N mock-data jump.
  private scheduleReveal(): void {
    $$.measure(() =>
      $$.mutate(() => this.pagelet?.classList.add("blog-ready")),
    );
  }
}

export namespace BlogIndexController {
  export type State = {
    searchQuery: string;
    categoryFilter: Option<BlogCategory>;
    displayCount: number;
    totalPosts: number;
    featuredPosts: BlogPost[];
    posts: BlogPost[];
  };

  export function stateFromPageModel(page: PageModel): Option<State> {
    for (const node of page.nodes) {
      if (node.kind === "post-list") return stateFromNode(node);
    }
    return null;
  }

  export function stateFromNode(node: PageNode): State {
    const posts = postsFromProp(node.props, "posts");
    const featuredPosts = postsFromProp(node.props, "featuredPosts");
    return {
      posts,
      featuredPosts: featuredPosts.length > 0
        ? featuredPosts
        : posts.filter((post) => post.featured).slice(0, 6),
      displayCount: getNumber(node.props, "displayCount", 10),
      totalPosts: getNumber(node.props, "totalPosts", 0),
      searchQuery: getString(node.props, "searchQuery"),
      categoryFilter: getCategory(node.props, "categoryFilter"),
    };
  }
}

function blogMockCookiePresent(): boolean {
  return document.cookie.split(";").some((entry) => {
    const trimmed = entry.trim();
    if (!trimmed.startsWith("lona_blog_mock=")) return false;
    const value = trimmed.slice("lona_blog_mock=".length).trim();
    return value !== "" && value !== "0" && value !== "off" && value !== "false";
  });
}

function postsFromProp(object: JsonObject, key: string): BlogPost[] {
  const posts: BlogPost[] = [];
  for (const value of getArray(object, key)) {
    const post = postFromJson(value);
    if (post) posts.push(post);
  }
  return posts;
}

function postFromJson(value: JsonValue): Option<BlogPost> {
  const object = asObject(value);
  if (!object) return null;

  return {
    slug: getString(object, "slug"),
    title: getString(object, "title"),
    date: getString(object, "date"),
    summary: getString(object, "summary"),
    heroImage: getString(object, "heroImage"),
    authorName: getString(object, "authorName"),
    authorRole: getOptionString(object, "authorRole"),
    category: getString(object, "category") as BlogCategory,
    featured: getBoolean(object, "featured"),
    tags: getStringArray(object, "tags"),
    content: getOptionString(object, "content"),
    contentHtml: getOptionString(object, "contentHtml"),
  };
}

function getArray(object: JsonObject, key: string): JsonValue[] {
  const value = object[key];
  return Array.isArray(value) ? value : [];
}

function asObject(value: JsonValue): Option<JsonObject> {
  if (value === null) return null;
  if (Array.isArray(value)) return null;
  if (typeof value !== "object") return null;
  return value;
}

function getString(object: JsonObject, key: string): string {
  const value = object[key];
  return typeof value === "string" ? value : "";
}

function getOptionString(object: JsonObject, key: string): Option<string> {
  const value = object[key];
  return typeof value === "string" ? value : null;
}

function getCategory(object: JsonObject, key: string): Option<BlogCategory> {
  const value = object[key];
  return typeof value === "string" ? value as BlogCategory : null;
}

function getBoolean(object: JsonObject, key: string): boolean {
  const value = object[key];
  return typeof value === "boolean" ? value : false;
}

function getNumber(object: JsonObject, key: string, fallback: number): number {
  const value = object[key];
  return typeof value === "number" ? value : fallback;
}

function getStringArray(object: JsonObject, key: string): string[] {
  const strings: string[] = [];
  for (const value of getArray(object, key)) {
    if (typeof value === "string") strings.push(value);
  }
  return strings;
}
